import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign, IAudienceBreakdown, Targeting } from '@feathr/blackbox';
import { toast } from '@feathr/components';
import type { ListResponse } from '@feathr/rachis';
import { useStore } from '@feathr/report_components/state';

interface IUseAudienceBreakdownProps {
  targetings: ListResponse<Targeting>;
  campaign: Campaign;
}

/** The relative size of the lookalike audience that can be targeted. */
export type TLookalikeAudienceSize = number | 'Narrow' | 'Focused' | 'Moderate' | 'Broad' | 'Wide';

export interface IAudienceBreakdownReturn {
  numTargetedReachableCookies: IAudienceBreakdown['num_persons_reachable']['device'];
  numTargetedReachableEmails: IAudienceBreakdown['num_persons_reachable']['email'];
  targetedLookalikeAudienceSize: IAudienceBreakdown['num_persons_reachable']['lookalike_targeted'];
  actualLookalikeAudienceSize: IAudienceBreakdown['num_persons_reachable']['lookalike_actual'];
  numTargetedReachableUsers: IAudienceBreakdown['num_persons_reachable']['total'];
  numTargetedReachableDevices: IAudienceBreakdown['num_devices_reachable']['total_targetable'];
  numNewDevices: IAudienceBreakdown['num_devices_reachable']['estimated_new'];
  numDevicesPerPerson: IAudienceBreakdown['num_devices_reachable']['avg_per_person'];
  lookalikeAudienceSize?: number;
}

interface IUseAudienceBreakdown extends IAudienceBreakdownReturn {
  isLoading: boolean;
  isEmpty: boolean;
  context: 'wizard';
  lookalikeAudienceSize?: number;
}

/**
 * Hook to fetch audience breakdown data for targets step.
 * This can be used in the parent component and shared with child components.
 */
function useAudienceBreakdown({
  targetings,
  campaign,
}: IUseAudienceBreakdownProps): Readonly<IUseAudienceBreakdown> {
  const { t } = useTranslation();
  const { Segments, Targetables } = useStore();
  const [isLoading, setIsLoading] = useState(false);
  const [audienceBreakdown, setAudienceBreakdown] = useState<IAudienceBreakdown | null>(null);
  const { isSegmentCampaign, isOmniReachCampaign } = campaign;

  // Cookies and devices
  const cookies = audienceBreakdown?.num_persons_reachable?.device ?? 0;
  const emails = audienceBreakdown?.num_persons_reachable?.email ?? 0;

  // The total targeted lookalike audience
  const lookalike = audienceBreakdown?.num_persons_reachable?.lookalike_targeted ?? 0;

  // The total actual lookalike audience (after campaign is published)
  const lookalikeActual = audienceBreakdown?.num_persons_reachable?.lookalike_actual ?? 0;

  const numReachableUsers = audienceBreakdown?.num_persons_reachable?.total ?? 0;
  const deviceDiscovery = audienceBreakdown?.num_devices_reachable?.estimated_new ?? 0;
  const deviceIds = audienceBreakdown?.num_devices_reachable?.total_targetable ?? 0;
  const devicesPerPerson = audienceBreakdown?.num_devices_reachable?.avg_per_person ?? 0;

  // get included segment IDs, filtering out ephemeral segments
  // segments being fetched have isEphemeral=true but isPending=true, so we keep those
  const includedSegmentIds = targetings.models
    .filter(
      (t) => t.get('is_archived') !== true && t.get('kind') === 'segment' && t.get('included'),
    )
    .map((t) => t.get('target_data'))
    .filter((segmentId): segmentId is string => {
      if (!segmentId) {
        return false;
      }
      const segment = Segments.get(segmentId);
      // Filter out ephemeral segments to avoid 400 errors from backend
      // keep segments that are being fetched (isPending=true) as they exist on the backend
      return !(segment.isEphemeral && !segment.isPending);
    });

  // Get excluded segment IDs, filtering out ephemeral segments
  const excludedSegmentIds = targetings.models
    .filter(
      (t) => t.get('is_archived') !== true && t.get('kind') === 'segment' && !t.get('included'),
    )
    .map((t) => t.get('target_data'))
    .filter((segmentId): segmentId is string => {
      if (!segmentId) {
        return false;
      }
      const segment = Segments.get(segmentId);
      return !(segment.isEphemeral && !segment.isPending);
    });

  const lookalikeTargeting = targetings.models.find(
    (targeting) => targeting.get('kind') === 'omni_lookalike' && !targeting.get('is_archived'),
  );

  const lookalikeTargetingId = lookalikeTargeting?.id;
  const lookalikeTargetableId = lookalikeTargeting?.get('target_data');
  const lookalikeTargetable = lookalikeTargetableId
    ? Targetables.get(lookalikeTargetableId)
    : undefined;

  const audienceSize = lookalikeTargetingId ? lookalikeTargetable?.get('audience_size') : undefined;

  const isEmpty = includedSegmentIds.length === 0 || (emails === 0 && cookies === 0);

  // Omni campaign is included in segment campaign
  const shouldFetchAudienceBreakdown =
    isSegmentCampaign && !isOmniReachCampaign && !targetings.isPending && includedSegmentIds.length;

  useEffect(() => {
    async function fetchAudienceBreakdown(): Promise<void> {
      // Don't fetch if targetings are still loading
      if (targetings.isPending) {
        return;
      }

      setIsLoading(true);

      try {
        const response = await Segments.getAudienceBreakdown({
          included_segment_ids: includedSegmentIds,
          excluded_segment_ids: excludedSegmentIds,
          // We do not want to pass this Id if it's ephemeral because the backend is unaware of it
          lookalike_targeting_id: lookalikeTargeting?.isEphemeral
            ? undefined
            : lookalikeTargetingId,
          campaign_type: campaign.get('_cls'),
          mode: campaign.get('mode'),
        });

        setAudienceBreakdown(response);
      } catch (error) {
        toast(
          t("We couldn't fetch audience breakdown. Try again later.", {
            error,
          }),
          {
            type: 'error',
          },
        );
        setAudienceBreakdown(null);
      } finally {
        setIsLoading(false);
      }
    }

    // Only try to get the breakdown if it's an omni/retargeting campaign with included segments
    if (shouldFetchAudienceBreakdown) {
      fetchAudienceBreakdown();
    }
  }, [
    targetings.isPending,
    JSON.stringify(includedSegmentIds) + JSON.stringify(excludedSegmentIds),
    campaign.get('mode'),
  ]);

  return {
    numTargetedReachableUsers: numReachableUsers,
    numTargetedReachableEmails: emails,
    numTargetedReachableCookies: cookies,
    targetedLookalikeAudienceSize: lookalike,
    actualLookalikeAudienceSize: lookalikeActual,
    numTargetedReachableDevices: deviceIds,
    numNewDevices: deviceDiscovery,
    numDevicesPerPerson: devicesPerPerson,
    lookalikeAudienceSize: audienceSize,
    isEmpty,
    isLoading: isLoading || targetings.isPending,
    context: 'wizard',
  };
}

export default useAudienceBreakdown;
